import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, ArrowRight, ImageOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import ImageUpload from './ImageUpload';
import { getBeforeAfter, createBeforeAfter, deleteBeforeAfter } from '../lib/api';
import { toast } from 'sonner';

const BeforeAfterManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ before_image: '', after_image: '', service_title: '', description: '' });

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getBeforeAfter());
    } catch (e) {
      toast.error('Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.before_image || !form.after_image || !form.service_title) {
      toast.error('Completa las imágenes y el título del servicio');
      return;
    }
    setSaving(true);
    try {
      await createBeforeAfter({ ...form, order: items.length });
      toast.success('Comparación agregada');
      setForm({ before_image: '', after_image: '', service_title: '', description: '' });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error('Error al agregar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta comparación?')) return;
    try {
      await deleteBeforeAfter(id);
      toast.success('Eliminado');
      setItems(items.filter((i) => i.id !== id));
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Antes y Después</h2>
          <p className="text-white/60 text-sm">{items.length} comparaciones publicadas</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-beforeafter-button"
          className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar comparación
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-[#0F1D33]/60 border border-white/10 rounded-2xl p-6 mb-6 space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <ImageUpload
              value={form.before_image}
              onChange={(url) => setForm({ ...form, before_image: url })}
              label="Imagen ANTES"
              testId="ba-before"
            />
            <ImageUpload
              value={form.after_image}
              onChange={(url) => setForm({ ...form, after_image: url })}
              label="Imagen DESPUÉS"
              testId="ba-after"
            />
          </div>
          <div>
            <label className="text-white/70 text-sm mb-2 block">Título del servicio</label>
            <Input
              value={form.service_title}
              onChange={(e) => setForm({ ...form, service_title: e.target.value })}
              placeholder="Ej: Pulido de pintura"
              data-testid="ba-title"
              className="bg-[#0A1628]/60 border-white/20 text-white rounded-xl"
            />
          </div>
          <div>
            <label className="text-white/70 text-sm mb-2 block">Descripción (opcional)</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Breve descripción del trabajo"
              rows={2}
              data-testid="ba-description"
              className="bg-[#0A1628]/60 border-white/20 text-white rounded-xl resize-none"
            />
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} data-testid="save-beforeafter-button" className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/20 text-white rounded-xl">
              Cancelar
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#60A5FA] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          <ImageOff className="w-12 h-12 mx-auto mb-4" />
          <p>No hay comparaciones todavía.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item.id} className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0F1D33]/50">
              <div className="relative aspect-[16/10] grid grid-cols-2">
                <img src={item.before_image} alt="antes" className="w-full h-full object-cover" />
                <img src={item.after_image} alt="después" className="w-full h-full object-cover" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-[#60A5FA] rounded-full flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
                <span className="absolute bottom-2 left-2 text-white/70 text-xs bg-black/50 px-2 py-0.5 rounded">Antes</span>
                <span className="absolute bottom-2 right-2 text-[#60A5FA] text-xs bg-black/50 px-2 py-0.5 rounded">Después</span>
              </div>
              <div className="p-4">
                <p className="text-white font-medium">{item.service_title}</p>
                {item.description && <p className="text-white/50 text-sm mt-1">{item.description}</p>}
              </div>
              <button
                onClick={() => handleDelete(item.id)}
                data-testid={`delete-beforeafter-${item.id}`}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500/90 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BeforeAfterManager;
